import React, {
  FC,
  useState,
  KeyboardEvent,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import get from 'lodash/get';
import MenuItem from '../MenuItem';
import { Option } from '../../../types/SelectInput';
import styles from './MenuListView.module.css';
import { translate } from '../../../localization';

type MenuListViewProps = {
  list: Option[];
  searchTextValue?: string;
  onChange: (value: string | string[]) => void;
  multiSelect?: boolean;
  hasOptionSelected: (option: string) => boolean;
  valueProperty: string;
  selectedValue?: string | string[];
  enableSelectAll?: boolean;
};

const MenuListView: FC<MenuListViewProps> = React.memo(
  ({
    list,
    searchTextValue = '',
    onChange = () => null,
    multiSelect = false,
    hasOptionSelected,
    valueProperty,
    selectedValue,
    enableSelectAll = false,
  }) => {
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(
      selectedValue?.length === list?.length
    );
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    const updatedList = useMemo(() => {
      return enableSelectAll && (!searchTextValue || searchTextValue == null)
        ? [
            { id: 'all', label: `${translate('select_all')}`, name: 'all' },
            ...list,
          ]
        : list;
    }, [enableSelectAll, list, searchTextValue]);

    const checkIfOptionIsSelected = (option: Option) => {
      const optionValue = get(option, valueProperty, '') as string;

      if (optionValue === 'all') {
        return isSelectAllChecked;
      }

      return hasOptionSelected(optionValue);
    };

    const handleItemClick = (value: string) => {
      if (multiSelect) {
        /** Used for the "Select All" functionality. */
        let isToCheckSelectAll = false;
        let newSelectedValues: string[] = [];

        if (value === 'all') {
          // for the "Select All" functionality
          if (!isSelectAllChecked) {
            isToCheckSelectAll = true;
            newSelectedValues = list?.map(li => li?.name as string);
          }
        } else {
          const isValueAlreadySelected =
            (selectedValue as string[])?.find(option => option === value) !==
            undefined;

          if (isValueAlreadySelected) {
            newSelectedValues = (selectedValue as string[])?.filter(
              option => option !== value
            );
          } else {
            newSelectedValues = [...(selectedValue as string[])];
            newSelectedValues.push(value);
            isToCheckSelectAll = newSelectedValues?.length === list?.length;
          }
        }

        setIsSelectAllChecked(isToCheckSelectAll);
        onChange(newSelectedValues);
      } else {
        onChange(value as string);
      }
    };

    const scrollIntoView = (index: number) => {
      const element = itemRefs?.current?.[index];
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      let newIndex = activeIndex; // Initialize newIndex with the current activeIndex

      switch (event.key) {
        case 'ArrowDown':
          newIndex = (activeIndex + 1) % updatedList.length;
          break;

        case 'ArrowUp':
          newIndex =
            (activeIndex - 1 + updatedList.length) % updatedList.length;
          break;

        case 'Space':
        case 'Enter':
          if (updatedList?.[activeIndex]) {
            handleItemClick(
              updatedList?.[activeIndex]?.[valueProperty] as string
            );
          }

          return; // Prevent further execution and scrolling on selection

        default:
          return; // Exit if the key is not handled
      }

      setActiveIndex(newIndex); // Update the activeIndex state
      scrollIntoView(newIndex); // Scroll the newly active item into view
      event.preventDefault(); // Prevent default key actions
    };

    const setItemRef = (itemIndex: number, el: HTMLDivElement | null) => {
      itemRefs.current[itemIndex] = el;
    };

    useEffect(() => {
      itemRefs.current = itemRefs?.current?.slice(0, updatedList?.length);
    }, [updatedList?.length]);

    return (
      <div
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={styles['menu-list-view']}
        role='none'
      >
        {updatedList?.map((option, index) => (
          <div ref={el => setItemRef(index, el)} key={option?.id}>
            <MenuItem
              key={option?.id}
              isActive={activeIndex === index}
              onClick={handleItemClick}
              selectable={multiSelect}
              isSelected={checkIfOptionIsSelected(option)}
              label={get(option, 'label', '')}
              value={get(option, valueProperty, '') as string}
              isDisabled={option?.isDisabled as boolean}
            />
          </div>
        ))}
      </div>
    );
  }
);

MenuListView.displayName = 'MenuListView';

export default MenuListView;
